"use client";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { getCurrencySymbol } from "../utils/currency/getCurrencySymbol";
import { PrimaryButton } from "../components/buttons/primaryButton/PrimaryButton.tsx";
import { useAppSelector } from "../hooks/redux/useRedux";
import { selectUser } from "../redux/slices/profileSlice/ProfileSlice";

const API_URL = REST_API_URL;
const options = ["Day", "Month"];

export default function IncomeChart() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const [mode, setMode] = useState("Month");
  const [open, setOpen] = useState(false);

  const [dataDay, setDataDay] = useState([]);
  const [dataMonth, setDataMonth] = useState([]);
  const user = useAppSelector(selectUser);
  const currencySymbol = getCurrencySymbol(user?.currency || "USD");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const chartData = mode === "Day" ? dataDay : dataMonth;

  const headers = useMemo(() => {
    if (!token) return null;
    return buildHeaders();
  }, [token]);

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token || !headers) return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(`${API_URL}/client/profile-analytics/`, {
          headers,
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setDataDay(
          (data["By Day"] || []).map((item) => ({
            name: item.label,
            income: item.total || 0,
            isCurrent: item.is_current,
            percentage: item.percentage_change,
          })),
        );

        setDataMonth(
          (data["By Month"] || []).map((item) => ({
            name: item.label,
            income: item.total || 0,
            isCurrent: item.is_current,
            percentage: item.percentage_change,
          })),
        );
      } catch (err) {
        console.error("❌ Failed to load analytics:", err);
        setError(t("home.analyticsLoadError"));
        setDataDay([]);
        setDataMonth([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [token, headers, navigate]);

  const total = chartData.reduce((sum, i) => sum + (i.income || 0), 0);

  return (
    <div className="rounded-xl bg-white shadow p-6 w-full relative h-full flex flex-col justify-between">
      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="font-bold text-2xl text-gray-800">
            {t("home.totalIncome")}
          </p>

          {loading ? (
            <p className="mt-4 text-neutral-400">{t("home.loading")}</p>
          ) : (
            <p className="font-normal text-[30px] mt-4 text-gray-800">
              {currencySymbol}
              {total.toLocaleString()}
            </p>
          )}
        </div>

        <div className="relative">
          <PrimaryButton
            onClick={() => setOpen(!open)}
            variant="outline"
            size="small"
            label={t("home.byMode", {
              mode: t(`home.by${mode}`).replace(/^По\s|^By\s/, ""),
            })}
            rightIcon={
              <ChevronDownIcon
                className={`w-4 h-4 transition ${open ? "rotate-180" : ""}`}
              />
            }
          />

          {open && (
            <div className="absolute right-0 mt-2 w-32 rounded-lg bg-white shadow-md border z-10">
              {options.map((opt) => (
                <div
                  key={opt}
                  onClick={() => {
                    setMode(opt);
                    setOpen(false);
                  }}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#020637]/8 ${
                    mode === opt
                      ? "text-[#020637] font-medium"
                      : "text-neutral-700"
                  }`}
                >
                  {t(`home.by${opt}`)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-red-500 text-sm mb-2 italic">{error}</p>}

      {loading ? (
        <div className="flex justify-center items-center h-[250px] text-neutral-400">
          {t("home.loadingChart")}
        </div>
      ) : chartData.length === 0 ? (
        <p className="text-neutral-400 text-center h-[250px] flex items-center justify-center">
          {t("home.noIncomeData")}
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" axisLine={false} tickLine={false} />
            <Tooltip
              cursor={{ fill: "transparent" }}
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="bg-[#020637] text-white px-3 py-1 rounded-md text-sm shadow">
                    {currencySymbol}
                    {payload[0].value}
                    {payload[0].payload.percentage !== null && (
                      <span className="ml-2 text-xs text-gray-200">
                        ({payload[0].payload.percentage > 0 ? "+" : ""}
                        {payload[0].payload.percentage}%)
                      </span>
                    )}
                  </div>
                ) : null
              }
            />
            <Bar
              dataKey="income"
              radius={[20, 20, 20, 20]}
              fill="#E5E7EB"
              barSize={45}
              minPointSize={6}
              activeBar={{ fill: "#1a2a8a" }}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
